import React, { useState, useMemo, useEffect } from 'react';
import { 
  CheckCircle, 
  Clock, 
  XCircle, 
  Activity, 
  Building, 
  Lock, 
  Shield, 
  User, 
  Calendar, 
  TrendingUp, 
  Eye, 
  EyeOff, 
  AlertTriangle, 
  Database, 
  Users, 
  FileText, 
  BarChart3,
  Info,
  Settings,
  RefreshCw,
  Target,
  Zap,
  Award,
  Layers
} from 'lucide-react';

// 🏢 MULTI-TENANT RESERVATION STATISTICS COMPONENT
const ReservationStatistics = ({ 
  reservations = [],
  // 🔒 Multi-tenant props (required for full functionality)
  currentUser,
  currentCompany,
  tenantContext,
  dataIsolationActive = false,
  validateCompanyAccess,
  // 🎛️ Feature configuration props
  showCompanyBreakdown = true,
  showAdvancedStats = false,
  showTenantMetrics = true,
  showSecurityWarnings = true,
  enableRealTimeUpdates = true,
  compactMode = false
}) => {
  // 🎛️ UI State Management
  const [showDetailedStats, setShowDetailedStats] = useState(false);
  const [showTenantMetricsPanel, setShowTenantMetricsPanel] = useState(false);
  const [showSecurityPanel, setShowSecurityPanel] = useState(false);
  const [securityWarnings, setSecurityWarnings] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [dataHealth, setDataHealth] = useState(100);

  // 🔒 MULTI-TENANT DATA VALIDATION AND FILTERING
  const validatedReservations = useMemo(() => {
    const warnings = [];
    let validReservations = [];
    let crossCompanyCount = 0;
    let legacyCount = 0;

    console.log('🔍 ReservationStatistics: Validating', reservations.length, 'reservations for company:', currentCompany?.name);

    if (!reservations || reservations.length === 0) {
      return { 
        data: [], 
        warnings: [], 
        crossCompanyCount: 0, 
        legacyCount: 0,
        healthScore: 100 
      };
    }

    // Validate multi-tenant context
    if (!currentUser || !currentCompany) {
      warnings.push('Missing authentication context - multi-tenant features unavailable');
    }

    if (!dataIsolationActive) {
      warnings.push('Data isolation not active - cross-company data may be visible');
    }

    reservations.forEach((reservation, index) => {
      // Check for company ID presence (legacy data detection)
      if (!reservation.companyId) {
        warnings.push(`Reservation #${index + 1}: Missing company context (legacy data)`);
        legacyCount++;
        validReservations.push({ ...reservation, isLegacy: true, securityRisk: 'low' });
        return;
      }

      // Validate company access
      if (validateCompanyAccess && !validateCompanyAccess(reservation.companyId)) {
        warnings.push(`Reservation #${index + 1}: Failed company access validation`);
        crossCompanyCount++;
        return; // Skip invalid data
      }

      // Check if reservation belongs to current company
      if (tenantContext && reservation.companyId !== tenantContext.companyId) {
        warnings.push(`Reservation #${index + 1}: Belongs to company ${reservation.companyId}`);
        crossCompanyCount++;
        return; // Skip different company data
      }

      // Check data integrity
      let securityRisk = 'none';
      if (!reservation.createdBy || !reservation.createdByUsername) {
        securityRisk = 'low';
      }
      if (!reservation.tenantIsolated) {
        securityRisk = 'medium';
      }

      validReservations.push({ 
        ...reservation, 
        isValidated: true, 
        securityRisk,
        belongsToCurrentCompany: true 
      });
    });

    // Calculate health score
    let healthScore = 100;
    if (legacyCount > 0) healthScore -= Math.min(30, (legacyCount / reservations.length) * 50);
    if (crossCompanyCount > 0) healthScore -= Math.min(40, (crossCompanyCount / reservations.length) * 60);
    if (warnings.length > 5) healthScore -= 20;
    if (!dataIsolationActive) healthScore -= 25;

    setSecurityWarnings(warnings);
    setDataHealth(Math.max(0, Math.round(healthScore)));

    console.log('✅ ReservationStatistics: Validated', validReservations.length, 'reservations, Health Score:', Math.round(healthScore));

    return { 
      data: validReservations, 
      warnings, 
      crossCompanyCount, 
      legacyCount,
      healthScore: Math.max(0, Math.round(healthScore))
    };
  }, [reservations, tenantContext, validateCompanyAccess, currentUser, currentCompany, dataIsolationActive]);

  // 📊 BASIC STATISTICS CALCULATION
  const basicStats = useMemo(() => {
    const data = validatedReservations.data;
    const totalCount = data.length;
    const bookedCount = data.filter(res => res.availabilityStatus === 'booked').length;
    const pendingCount = data.filter(res => res.availabilityStatus === 'pending').length;
    const cancelCount = data.filter(res => res.availabilityStatus === 'cancel').length;
    
    // Calculate percentages (avoid division by zero)
    const bookedPercentage = totalCount ? Math.round((bookedCount / totalCount) * 100) : 0;
    const pendingPercentage = totalCount ? Math.round((pendingCount / totalCount) * 100) : 0;
    const cancelPercentage = totalCount ? Math.round((cancelCount / totalCount) * 100) : 0;

    // Payment statistics
    const paidCount = data.filter(res => res.rentalPaymentStatus === 'paid').length;
    const unpaidCount = data.filter(res => res.rentalPaymentStatus === 'not paid').length;

    return {
      totalCount,
      bookedCount,
      pendingCount,
      cancelCount,
      bookedPercentage,
      pendingPercentage,
      cancelPercentage,
      paidCount,
      unpaidCount,
      conversionRate: totalCount ? Math.round((bookedCount / totalCount) * 100) : 0
    };
  }, [validatedReservations.data]);

  // 📈 ADVANCED MULTI-TENANT STATISTICS
  const advancedStats = useMemo(() => {
    const data = validatedReservations.data;
    
    if (data.length === 0) {
      return {
        companyReservations: 0,
        legacyReservations: 0,
        userBreakdown: {},
        dateRangeStats: {},
        tenantHealthScore: validatedReservations.healthScore,
        recentReservations: 0,
        averagePerUser: 0,
        securityRiskLevels: {},
        bikeUtilization: {},
        locationStats: {}
      };
    }

    // Company-specific reservations
    const companyReservations = data.filter(res => res.companyId === tenantContext?.companyId).length;
    const legacyReservations = data.filter(res => res.isLegacy).length;

    // User breakdown
    const userBreakdown = data.reduce((acc, res) => {
      const user = res.createdByUsername || 'Unknown';
      acc[user] = (acc[user] || 0) + 1;
      return acc;
    }, {});

    // Security risk levels
    const securityRiskLevels = data.reduce((acc, res) => {
      const risk = res.securityRisk || 'none';
      acc[risk] = (acc[risk] || 0) + 1;
      return acc;
    }, {});

    // Bike utilization (if motorbike data available)
    const bikeUtilization = data.reduce((acc, res) => {
      if (res.motorbikeNumber) {
        acc[res.motorbikeNumber] = (acc[res.motorbikeNumber] || 0) + 1;
      }
      return acc;
    }, {});

    // Location statistics
    const locationStats = {
      pickupLocations: data.reduce((acc, res) => {
        const location = res.pickupLocation || 'Unknown';
        acc[location] = (acc[location] || 0) + 1;
        return acc;
      }, {}),
      dropoffLocations: data.reduce((acc, res) => {
        const location = res.dropoffLocation || 'Unknown';
        acc[location] = (acc[location] || 0) + 1;
        return acc;
      }, {})
    };

    // Date range statistics (last 30 days)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    const recentReservations = data.filter(res => {
      if (!res.createdAt) return false;
      const createdDate = res.createdAt.toDate ? res.createdAt.toDate() : new Date(res.createdAt);
      return createdDate >= thirtyDaysAgo;
    });

    const averagePerUser = Object.keys(userBreakdown).length > 0 ? 
      Math.round(data.length / Object.keys(userBreakdown).length) : 0;

    return {
      companyReservations,
      legacyReservations,
      userBreakdown,
      recentReservations: recentReservations.length,
      tenantHealthScore: validatedReservations.healthScore,
      averagePerUser,
      securityRiskLevels,
      bikeUtilization,
      locationStats
    };
  }, [validatedReservations.data, validatedReservations.healthScore, tenantContext]);

  // 🔄 REAL-TIME UPDATE EFFECT
  useEffect(() => {
    if (enableRealTimeUpdates) {
      setLastUpdated(new Date());
      
      // Auto-refresh every 30 seconds if data is active
      const interval = setInterval(() => {
        if (validatedReservations.data.length > 0) {
          setLastUpdated(new Date());
        }
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [validatedReservations.data.length, enableRealTimeUpdates]);

  // 🎨 COMPANY THEME COLORS
  const getCompanyThemeColor = () => {
    if (!currentCompany?.name) return 'blue';
    
    // Generate consistent colors based on company name
    const colors = ['blue', 'purple', 'green', 'orange', 'pink', 'indigo', 'cyan', 'emerald'];
    const hash = currentCompany.name.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    return colors[Math.abs(hash) % colors.length];
  };

  const companyColor = getCompanyThemeColor();

  // 🎨 THEME-BASED CSS CLASSES
  const getThemeClasses = (variant = 'primary') => {
    const themes = {
      blue: {
        primary: 'from-blue-500 to-blue-600',
        secondary: 'bg-blue-900/30 border-blue-700 text-blue-400',
        light: 'bg-blue-900/10 border-blue-800/30'
      },
      purple: {
        primary: 'from-purple-500 to-purple-600',
        secondary: 'bg-purple-900/30 border-purple-700 text-purple-400',
        light: 'bg-purple-900/10 border-purple-800/30'
      },
      green: {
        primary: 'from-green-500 to-green-600',
        secondary: 'bg-green-900/30 border-green-700 text-green-400',
        light: 'bg-green-900/10 border-green-800/30'
      },
      orange: {
        primary: 'from-orange-500 to-orange-600',
        secondary: 'bg-orange-900/30 border-orange-700 text-orange-400',
        light: 'bg-orange-900/10 border-orange-800/30'
      },
      pink: {
        primary: 'from-pink-500 to-pink-600',
        secondary: 'bg-pink-900/30 border-pink-700 text-pink-400',
        light: 'bg-pink-900/10 border-pink-800/30'
      }
    };
    
    return themes[companyColor] ? themes[companyColor][variant] : themes.blue[variant];
  };

  // 🔄 REFRESH DATA HANDLER
  const handleRefreshData = () => {
    setLastUpdated(new Date());
    // Trigger any parent refresh logic if needed
    console.log('🔄 ReservationStatistics: Manual refresh triggered');
  };

  // 🚫 PERMISSION CHECK FOR ADVANCED FEATURES
  const hasAdvancedPermissions = () => {
    if (!currentUser) return false;
    return currentUser.role === 'admin' || 
           currentUser.role === 'manager' || 
           currentUser.permissions?.includes('advancedAnalytics');
  };

  // 📱 RESPONSIVE GRID CLASSES
  const gridClasses = compactMode ? 
    'grid grid-cols-2 lg:grid-cols-4 gap-3' : 
    'grid grid-cols-1 sm:grid-cols-4 gap-4';

  return (
    <div className="mb-6 bg-gray-800 rounded-xl shadow-lg border border-gray-700 overflow-hidden">
      {/* 🏢 MULTI-TENANT HEADER */}
      <div className="p-4 border-b border-gray-700 bg-gradient-to-r from-gray-800 to-gray-900">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Activity className="text-purple-500" size={20} />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-500 to-pink-600">
              Booking Statistics
            </span>
            {dataIsolationActive && (
              <div className="ml-2 flex items-center text-green-400 text-xs">
                <Lock size={12} className="mr-1" />
                <span>Multi-Tenant</span>
              </div>
            )}
            {validatedReservations.healthScore < 90 && (
              <div className="ml-2 flex items-center text-yellow-400 text-xs">
                <AlertTriangle size={12} className="mr-1" />
                <span>Health: {validatedReservations.healthScore}%</span>
              </div>
            )}
          </h3>
          
          <div className="flex items-center space-x-3">
            {/* Company Badge */}
            {currentCompany && showCompanyBreakdown && (
              <div className={`flex items-center text-sm px-3 py-1 rounded-full border ${getThemeClasses('secondary')}`}>
                <Building size={14} className="mr-1" />
                <span>{currentCompany.name}</span>
              </div>
            )}
            
            {/* Refresh Button */}
            {enableRealTimeUpdates && (
              <button
                onClick={handleRefreshData}
                className="flex items-center gap-1 text-gray-400 hover:text-white transition-colors text-xs"
                title="Refresh statistics"
              >
                <RefreshCw size={14} />
                <span className="hidden sm:inline">Refresh</span>
              </button>
            )}
            
            {/* Tenant Metrics Toggle */}
            {showTenantMetrics && (
              <button
                onClick={() => setShowTenantMetricsPanel(!showTenantMetricsPanel)}
                className="flex items-center gap-1 text-gray-400 hover:text-white transition-colors text-xs"
                title="Toggle tenant metrics"
              >
                {showTenantMetricsPanel ? <EyeOff size={14} /> : <Eye size={14} />}
                <span className="hidden sm:inline">Metrics</span>
              </button>
            )}
            
            {/* Advanced Stats Toggle */}
            {showAdvancedStats && hasAdvancedPermissions() && (
              <button
                onClick={() => setShowDetailedStats(!showDetailedStats)}
                className="flex items-center gap-1 text-gray-400 hover:text-white transition-colors text-xs"
                title="Toggle detailed statistics"
              >
                <BarChart3 size={14} />
                <span className="hidden sm:inline">Advanced</span>
              </button>
            )}

            {/* Security Panel Toggle */}
            {showSecurityWarnings && securityWarnings.length > 0 && (
              <button
                onClick={() => setShowSecurityPanel(!showSecurityPanel)}
                className="flex items-center gap-1 text-red-400 hover:text-red-300 transition-colors text-xs"
                title="View security warnings"
              >
                <Shield size={14} />
                <span className="bg-red-900/30 px-2 py-0.5 rounded-full">
                  {securityWarnings.length}
                </span>
              </button>
            )}
          </div>
        </div>

        {/* 🔒 TENANT CONTEXT DISPLAY */}
        {showTenantMetricsPanel && tenantContext && (
          <div className="mt-3 pt-3 border-t border-gray-600">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
              <div className="bg-gray-700/50 rounded p-3">
                <div className="text-blue-300 font-medium flex items-center mb-1">
                  <Building size={12} className="mr-1" />
                  Company Context
                </div>
                <div className="text-gray-300">{tenantContext.companyName}</div>
                <div className="text-gray-400">ID: {tenantContext.companyId?.slice(-8)}</div>
                <div className="text-gray-400">Industry: {currentCompany?.industry || 'N/A'}</div>
              </div>
              <div className="bg-gray-700/50 rounded p-3">
                <div className="text-green-300 font-medium flex items-center mb-1">
                  <Shield size={12} className="mr-1" />
                  Data Security
                </div>
                <div className="text-gray-300">
                  {dataIsolationActive ? '✅ Isolated' : '⚠️ Not Isolated'}
                </div>
                <div className="text-gray-400">Health: {validatedReservations.healthScore}%</div>
                <div className="text-gray-400">
                  Warnings: {securityWarnings.length}
                </div>
              </div>
              <div className="bg-gray-700/50 rounded p-3">
                <div className="text-purple-300 font-medium flex items-center mb-1">
                  <User size={12} className="mr-1" />
                  User Context
                </div>
                <div className="text-gray-300">{tenantContext.username}</div>
                <div className="text-gray-400 capitalize">{tenantContext.userRole}</div>
                <div className="text-gray-400">
                  Permissions: {currentUser?.permissions?.length || 0}
                </div>
              </div>
              <div className="bg-gray-700/50 rounded p-3">
                <div className="text-orange-300 font-medium flex items-center mb-1">
                  <Database size={12} className="mr-1" />
                  Data Quality
                </div>
                <div className="text-gray-300">
                  {advancedStats.legacyReservations === 0 ? '✅ Clean' : '⚠️ Mixed'}
                </div>
                <div className="text-gray-400">
                  Legacy: {advancedStats.legacyReservations}
                </div>
                <div className="text-gray-400">
                  Validated: {advancedStats.companyReservations}
                </div>
              </div>
            </div>
            
            {/* Last Updated Info */}
            <div className="mt-3 pt-3 border-t border-gray-600 flex items-center justify-between text-xs text-gray-400">
              <div className="flex items-center">
                <RefreshCw size={12} className="mr-1" />
                <span>Last updated: {lastUpdated.toLocaleTimeString()}</span>
              </div>
              <div className="flex items-center">
                <Zap size={12} className="mr-1" />
                <span>Real-time: {enableRealTimeUpdates ? 'Active' : 'Disabled'}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 🚨 SECURITY WARNINGS PANEL */}
      {showSecurityPanel && securityWarnings.length > 0 && (
        <div className="px-4 py-3 bg-red-900/20 border-b border-red-700">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center text-red-400 font-medium">
              <AlertTriangle className="mr-2" size={16} />
              Multi-Tenant Security Warnings ({securityWarnings.length})
            </div>
            <button 
              onClick={() => setShowSecurityPanel(false)}
              className="text-red-400 hover:text-red-300"
              title="Close security panel"
            >
              <XCircle size={16} />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-red-300 text-xs max-h-32 overflow-y-auto">
            {securityWarnings.map((warning, index) => (
              <div key={index} className="flex items-start">
                <Shield size={10} className="mr-2 flex-shrink-0 mt-0.5" />
                <span>{warning}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-red-700 flex items-center justify-between text-xs">
            <div className="text-red-300">
              💡 Tip: Review data isolation settings and check Firebase security rules
            </div>
            <div className="text-red-400">
              Health Impact: -{Math.min(50, securityWarnings.length * 5)}%
            </div>
          </div>
        </div>
      )}
      
      {/* 📊 MAIN STATISTICS GRID */}
      <div className="p-4">
        <div className={gridClasses}>
          {/* Total Count with Multi-Tenant Context */}
          <div className="bg-gray-700/50 rounded-lg p-4 border border-gray-600 hover:bg-gray-700/70 transition-colors">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <p className="text-gray-400 text-sm flex items-center">
                  <FileText size={12} className="mr-1" />
                  Total Bookings
                </p>
                <p className="text-white text-2xl font-bold mt-1">{basicStats.totalCount}</p>
                {currentCompany && showCompanyBreakdown && (
                  <p className={`text-xs mt-1 ${getThemeClasses('secondary').includes('text-blue') ? 'text-blue-400' : 'text-purple-400'}`}>
                    {currentCompany.name}
                  </p>
                )}
                <div className="mt-2 flex items-center space-x-2 text-xs">
                  <span className="text-gray-400">
                    Conversion: {basicStats.conversionRate}%
                  </span>
                  {advancedStats.averagePerUser > 0 && (
                    <span className="text-gray-400">
                      Avg/User: {advancedStats.averagePerUser}
                    </span>
                  )}
                </div>
              </div>
              <div className="bg-blue-900/30 p-2 rounded-lg">
                <Activity size={24} className="text-blue-400" />
              </div>
            </div>
            
            {/* Data Quality Indicators */}
            <div className="mt-3 space-y-1">
              {advancedStats.legacyReservations > 0 && (
                <div className="flex items-center text-yellow-400 text-xs">
                  <AlertTriangle size={10} className="mr-1" />
                  <span>{advancedStats.legacyReservations} legacy records</span>
                </div>
              )}
              {validatedReservations.crossCompanyCount > 0 && (
                <div className="flex items-center text-red-400 text-xs">
                  <Shield size={10} className="mr-1" />
                  <span>{validatedReservations.crossCompanyCount} cross-company</span>
                </div>
              )}
              {dataIsolationActive && (
                <div className="flex items-center text-green-400 text-xs">
                  <Lock size={10} className="mr-1" />
                  <span>Data isolated</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Booked Count */}
          <div className="bg-green-900/10 rounded-lg p-4 border border-green-800/30 hover:bg-green-900/20 transition-colors">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <p className="text-gray-400 text-sm flex items-center">
                  <CheckCircle size={12} className="mr-1" />
                  Booked
                </p>
                <p className="text-white text-2xl font-bold mt-1">{basicStats.bookedCount}</p>
                <div className="flex items-center mt-2">
                  <div className="h-1.5 w-full bg-gray-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-green-500 to-green-600 transition-all duration-300" 
                      style={{ width: `${basicStats.bookedPercentage}%` }}
                    ></div>
                  </div>
                  <span className="text-gray-400 text-xs ml-2 min-w-[35px]">{basicStats.bookedPercentage}%</span>
                </div>
                <div className="mt-1 text-xs text-gray-400">
                  Success Rate: {basicStats.conversionRate}%
                </div>
              </div>
              <div className="bg-green-900/30 p-2 rounded-lg">
                <CheckCircle size={24} className="text-green-400" />
              </div>
            </div>
          </div>
          
          {/* Pending Count */}
          <div className="bg-yellow-900/10 rounded-lg p-4 border border-yellow-800/30 hover:bg-yellow-900/20 transition-colors">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <p className="text-gray-400 text-sm flex items-center">
                  <Clock size={12} className="mr-1" />
                  Pending
                </p>
                <p className="text-white text-2xl font-bold mt-1">{basicStats.pendingCount}</p>
                <div className="flex items-center mt-2">
                  <div className="h-1.5 w-full bg-gray-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-yellow-500 to-yellow-600 transition-all duration-300" 
                      style={{ width: `${basicStats.pendingPercentage}%` }}
                    ></div>
                  </div>
                  <span className="text-gray-400 text-xs ml-2 min-w-[35px]">{basicStats.pendingPercentage}%</span>
                </div>
                <div className="mt-1 text-xs text-yellow-400">
                  {basicStats.pendingCount > 0 ? 'Needs attention' : 'All clear'}
                </div>
              </div>
              <div className="bg-yellow-900/30 p-2 rounded-lg">
                <Clock size={24} className="text-yellow-400" />
              </div>
            </div>
          </div>
          
          {/* Canceled Count */}
          <div className="bg-red-900/10 rounded-lg p-4 border border-red-800/30 hover:bg-red-900/20 transition-colors">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <p className="text-gray-400 text-sm flex items-center">
                  <XCircle size={12} className="mr-1" />
                  Canceled
                </p>
                <p className="text-white text-2xl font-bold mt-1">{basicStats.cancelCount}</p>
                <div className="flex items-center mt-2">
                  <div className="h-1.5 w-full bg-gray-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-red-500 to-red-600 transition-all duration-300" 
                      style={{ width: `${basicStats.cancelPercentage}%` }}
                    ></div>
                  </div>
                  <span className="text-gray-400 text-xs ml-2 min-w-[35px]">{basicStats.cancelPercentage}%</span>
                </div>
                <div className="mt-1 text-xs text-red-400">
                  Loss Rate: {basicStats.cancelPercentage}%
                </div>
              </div>
              <div className="bg-red-900/30 p-2 rounded-lg">
                <XCircle size={24} className="text-red-400" />
              </div>
            </div>
          </div>
        </div>

        {/* 📈 DETAILED MULTI-TENANT STATISTICS */}
        {showDetailedStats && hasAdvancedPermissions() && (
          <div className="mt-6 pt-6 border-t border-gray-600">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-white font-medium flex items-center">
                <TrendingUp className="mr-2" size={16} />
                Advanced Multi-Tenant Analytics
              </h4>
              <div className="flex items-center text-xs text-gray-400">
                <Award className="mr-1" size={12} />
                <span>Admin Level Access</span>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Company Data Health */}
              <div className="bg-gray-700/30 rounded-lg p-4 border border-gray-600">
                <h5 className="text-gray-300 font-medium mb-3 flex items-center">
                  <Shield className="mr-2" size={14} />
                  Data Health Score
                </h5>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-sm">Overall Health</span>
                    <span className={`text-lg font-bold ${
                      validatedReservations.healthScore >= 90 ? 'text-green-400' :
                      validatedReservations.healthScore >= 70 ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                      {validatedReservations.healthScore}%
                    </span>
                  </div>
                  <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all duration-500 ${
                        validatedReservations.healthScore >= 90 ? 'bg-gradient-to-r from-green-500 to-green-600' :
                        validatedReservations.healthScore >= 70 ? 'bg-gradient-to-r from-yellow-500 to-yellow-600' :
                        'bg-gradient-to-r from-red-500 to-red-600'
                      }`}
                      style={{ width: `${validatedReservations.healthScore}%` }}
                    ></div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Company:</span>
                      <span className="text-green-400">{advancedStats.companyReservations}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Legacy:</span>
                      <span className="text-yellow-400">{advancedStats.legacyReservations}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Warnings:</span>
                      <span className="text-red-400">{securityWarnings.length}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Isolated:</span>
                      <span className={dataIsolationActive ? 'text-green-400' : 'text-red-400'}>
                        {dataIsolationActive ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* User Activity Breakdown */}
              <div className="bg-gray-700/30 rounded-lg p-4 border border-gray-600">
                <h5 className="text-gray-300 font-medium mb-3 flex items-center">
                  <Users className="mr-2" size={14} />
                  User Activity Breakdown
                </h5>
                <div className="space-y-2 max-h-40 overflow-y-auto custom-scrollbar">
                  {Object.entries(advancedStats.userBreakdown).length > 0 ? (
                    Object.entries(advancedStats.userBreakdown)
                      .sort(([,a], [,b]) => b - a)
                      .slice(0, 8)
                      .map(([username, count], index) => (
                        <div key={username} className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className={`w-2 h-2 rounded-full mr-2 ${
                              index === 0 ? 'bg-gold-400' :
                              index === 1 ? 'bg-silver-400' :
                              index === 2 ? 'bg-bronze-400' : 'bg-gray-500'
                            }`}></div>
                            <span className="text-gray-400 text-sm flex items-center">
                              <User size={10} className="mr-1" />
                              {username}
                            </span>
                          </div>
                          <div className="flex items-center">
                            <span className="text-white text-sm font-medium mr-2">{count}</span>
                            <div className="w-12 h-1 bg-gray-700 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-blue-500 to-purple-600"
                                style={{ 
                                  width: `${Math.min(100, (count / Math.max(...Object.values(advancedStats.userBreakdown))) * 100)}%` 
                                }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      ))
                  ) : (
                    <div className="text-gray-500 text-sm flex items-center justify-center py-4">
                      <Users size={16} className="mr-2" />
                      No user activity data available
                    </div>
                  )}
                </div>
                {Object.keys(advancedStats.userBreakdown).length > 8 && (
                  <div className="text-xs text-gray-400 mt-2 text-center">
                    ... and {Object.keys(advancedStats.userBreakdown).length - 8} more users
                  </div>
                )}
              </div>

              {/* Recent Activity & Performance */}
              <div className="bg-gray-700/30 rounded-lg p-4 border border-gray-600">
                <h5 className="text-gray-300 font-medium mb-3 flex items-center">
                  <Calendar className="mr-2" size={14} />
                  Recent Performance
                </h5>
                <div className="space-y-4">
                  {/* Last 30 Days */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-400 text-sm">Last 30 Days</span>
                      <span className="text-white text-lg font-bold">{advancedStats.recentReservations}</span>
                    </div>
                    <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-purple-500 to-purple-600 transition-all duration-500"
                        style={{ 
                          width: `${basicStats.totalCount > 0 ? (advancedStats.recentReservations / basicStats.totalCount) * 100 : 0}%` 
                        }}
                      ></div>
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      {basicStats.totalCount > 0 ? 
                        `${Math.round((advancedStats.recentReservations / basicStats.totalCount) * 100)}% of total reservations` :
                        'No recent activity'
                      }
                    </div>
                  </div>

                  {/* Performance Metrics */}
                  <div className="pt-3 border-t border-gray-600">
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div className="bg-gray-800/50 rounded p-2">
                        <div className="text-gray-400">Avg/User</div>
                        <div className="text-white font-medium">{advancedStats.averagePerUser}</div>
                      </div>
                      <div className="bg-gray-800/50 rounded p-2">
                        <div className="text-gray-400">Success Rate</div>
                        <div className="text-green-400 font-medium">{basicStats.conversionRate}%</div>
                      </div>
                      <div className="bg-gray-800/50 rounded p-2">
                        <div className="text-gray-400">Active Users</div>
                        <div className="text-blue-400 font-medium">{Object.keys(advancedStats.userBreakdown).length}</div>
                      </div>
                      <div className="bg-gray-800/50 rounded p-2">
                        <div className="text-gray-400">Data Quality</div>
                        <div className={`font-medium ${
                          validatedReservations.healthScore >= 90 ? 'text-green-400' : 
                          validatedReservations.healthScore >= 70 ? 'text-yellow-400' : 'text-red-400'
                        }`}>
                          {validatedReservations.healthScore >= 90 ? 'Excellent' : 
                           validatedReservations.healthScore >= 70 ? 'Good' : 'Needs Work'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 🚲 ADDITIONAL INSIGHTS (if bike data available) */}
            {Object.keys(advancedStats.bikeUtilization).length > 0 && (
              <div className="mt-6 bg-gray-700/20 rounded-lg p-4 border border-gray-600">
                <h5 className="text-gray-300 font-medium mb-3 flex items-center">
                  <Target className="mr-2" size={14} />
                  Resource Utilization
                </h5>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Bike Utilization */}
                  <div>
                    <h6 className="text-gray-400 text-sm mb-2">Top Bikes</h6>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {Object.entries(advancedStats.bikeUtilization)
                        .sort(([,a], [,b]) => b - a)
                        .slice(0, 5)
                        .map(([bike, count]) => (
                          <div key={bike} className="flex items-center justify-between text-sm">
                            <span className="text-gray-300 truncate">{bike}</span>
                            <span className="text-white font-medium ml-2">{count}</span>
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* Location Stats */}
                  <div>
                    <h6 className="text-gray-400 text-sm mb-2">Popular Locations</h6>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {Object.entries(advancedStats.locationStats.pickupLocations)
                        .sort(([,a], [,b]) => b - a)
                        .slice(0, 3)
                        .map(([location, count]) => (
                          <div key={location} className="flex items-center justify-between text-sm">
                            <span className="text-gray-300">{location}</span>
                            <span className="text-white font-medium">{count}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* 🔒 MULTI-TENANT STATUS FOOTER */}
        {dataIsolationActive && tenantContext && (
          <div className="mt-6 pt-4 border-t border-gray-600 bg-gradient-to-r from-green-600/10 to-blue-600/10 rounded-lg p-3">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center space-x-4">
                <div className="flex items-center text-green-400">
                  <Lock size={14} className="mr-1" />
                  <span>Data Isolated</span>
                </div>
                <div className="flex items-center text-blue-400">
                  <Building size={14} className="mr-1" />
                  <span>{tenantContext.companyName}</span>
                </div>
                <div className="flex items-center text-purple-400">
                  <Database size={14} className="mr-1" />
                  <span>{basicStats.totalCount} records</span>
                </div>
                <div className="flex items-center text-orange-400">
                  <Layers size={14} className="mr-1" />
                  <span>Health: {validatedReservations.healthScore}%</span>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                {securityWarnings.length === 0 ? (
                  <div className="flex items-center text-green-400 text-xs">
                    <CheckCircle size={12} className="mr-1" />
                    <span>Secure</span>
                  </div>
                ) : (
                  <div className="flex items-center text-yellow-400 text-xs">
                    <AlertTriangle size={12} className="mr-1" />
                    <span>{securityWarnings.length} warnings</span>
                  </div>
                )}
                
                {enableRealTimeUpdates && (
                  <div className="flex items-center text-blue-400 text-xs">
                    <RefreshCw size={12} className="mr-1" />
                    <span>Live</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 📊 EMPTY STATE WITH MULTI-TENANT CONTEXT */}
        {basicStats.totalCount === 0 && (
          <div className="mt-6 pt-6 border-t border-gray-600 text-center">
            <div className="bg-gray-700/30 rounded-lg p-8">
              <Activity size={48} className="text-gray-500 mx-auto mb-4" />
              <h4 className="text-gray-300 text-lg font-medium mb-2">No Reservations Found</h4>
              <p className="text-gray-500 text-sm mb-4">
                {currentCompany ? 
                  `No reservations found for ${currentCompany.name}. Start by creating your first reservation.` :
                  'No reservations available. Check your company context or create new reservations.'
                }
              </p>
              
              {/* Empty State Action Buttons */}
              <div className="flex items-center justify-center space-x-3 mt-4">
                {currentCompany && (
                  <div className={`inline-flex items-center px-4 py-2 rounded-full border text-sm ${getThemeClasses('secondary')}`}>
                    <Building size={14} className="mr-2" />
                    <span>{currentCompany.name}</span>
                  </div>
                )}
                
                {dataIsolationActive && (
                  <div className="inline-flex items-center text-green-400 text-sm bg-green-900/20 px-3 py-2 rounded-full border border-green-700">
                    <Lock size={12} className="mr-1" />
                    <span>Data Isolated</span>
                  </div>
                )}
              </div>

              {/* Helpful Tips */}
              <div className="mt-6 text-xs text-gray-400 space-y-1">
                <div className="flex items-center justify-center">
                  <Info size={12} className="mr-1" />
                  <span>Multi-tenant system is active and ready for data</span>
                </div>
                {tenantContext && (
                  <div>User: {tenantContext.username} | Role: {tenantContext.userRole}</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Custom Scrollbar Styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #374151;
          border-radius: 2px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #6B7280;
          border-radius: 2px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #9CA3AF;
        }
      `}</style>
    </div>
  );
};

export default ReservationStatistics;